import i18n from "i18n";
import * as Yup from "yup";
import { DebtSegment, FacilityFee } from "store/types/deal/debt";

const t = (key: string) => i18n.t(key, { ns: "debt" });

export const debtValidationSchema = Yup.object({
  is_facility: Yup.boolean().required(),
  is_withdrawal: Yup.boolean().required(),
  loan_amount: Yup.number()
    .required(() => t("errors.required"))
    .min(1, () => t("errors.mustBePositive")),
  start_date: Yup.string().required(() => t("errors.required")),
  maturity_date: Yup.string()
    .required(() => t("errors.required"))
    .test("after-start", () => t("errors.afterStartDate"), function (value) {
      return !value || !this.parent.start_date || this.parent.start_date.split("T")[0].localeCompare(value) <= 0;
    }),
  payment_term: Yup.string()
    .required(() => t("errors.required"))
    .oneOf(["fixed_total", "fixed_principal", "custom", "balloon", "partial_balloon"]),
  principal_indexation_payment: Yup.boolean().required(() => t("errors.required")),
  interest_based_on_principal: Yup.boolean().required(() => t("errors.required")),
  vat: Yup.number()
    .nullable()
    .min(0, () => t("errors.mustBePositive"))
    .max(100, () => t("errors.max100")),
  loan_status_frequency: Yup.string().nullable().oneOf(["month", "quarter", "semi-annual", "year"]),
  loan_first_status_date: Yup.string().nullable()
});

export const principalValidationSchema = Yup.object({
  principal_frequency: Yup.string()
    .required(() => t("errors.required"))
    .oneOf(["month", "quarter", "semi-annual", "year"]),
  principal_first_payment_date: Yup.string()
    .nullable()
    .when("is_withdrawal", {
      is: true,
      then: Yup.string().required(() => t("errors.principalPaymentDateRequired")),
      otherwise: Yup.string().nullable()
    }),
  principal_reference_index: Yup.string().nullable(),
  principal_link_percent: Yup.number()
    .required(() => t("errors.required"))
    .min(0, () => t("errors.mustBePositive"))
    .max(100, () => t("errors.max100")),
  principal_index_value: Yup.number().nullable(),
  balloon_payment: Yup.number()
    .nullable()
    .min(0, () => t("errors.mustBePositive")),
  principal_reference_day: Yup.string()
    .nullable()
    .when("principal_reference_index", {
      is: (index: string | null) => !!index,
      then: Yup.string().required(() => t("errors.principalReferenceDayRequired")),
      otherwise: Yup.string().nullable()
    }),
  custom_payments_file: Yup.mixed().when("custom_payments", {
    is: true,
    then: Yup.mixed().nullable()
  })
});

export const interestValidationSchema = Yup.object({
  interest_frequency: Yup.string()
    .required(() => t("errors.required"))
    .oneOf(["month", "quarter", "semi-annual", "year"]),
  interest_first_payment_date: Yup.string()
    .nullable()
    .when(["is_withdrawal", "base_rate", "interest_reference_index"], {
      is: (isWithdrawal: boolean, baseRate: number | null, index: string | null) =>
        isWithdrawal && baseRate !== null && (baseRate > 0 || !!index),
      then: Yup.string().required(() => t("errors.interestPaymentDateRequired")),
      otherwise: Yup.string().nullable()
    }),
  interest_reference_index: Yup.string()
    .nullable()
    .when("float_type", {
      is: (floatType: string) => floatType !== "fixed",
      then: Yup.string().required(() => t("errors.linkageRequiredNotFixed")),
      otherwise: Yup.string().nullable()
    }),
  interest_link_percent: Yup.number()
    .required(() => t("errors.required"))
    .min(0, () => t("errors.mustBePositive"))
    .max(100, () => t("errors.max100")),
  interest_index_value: Yup.number().nullable(),
  base_rate: Yup.number()
    .min(0, () => t("errors.mustBePositive"))
    .nullable(),
  float_type: Yup.string()
    .required(() => t("errors.required"))
    .oneOf(["spread", "linked", "fixed"]),
  interest_rate_fixing: Yup.string()
    .nullable()
    .oneOf(["in_arrears", "in_advance", "compounded", null])
    .when("float_type", {
      is: "spread",
      then: Yup.string().required(() => t("errors.rateFixingRequiredSpread")),
      otherwise: Yup.string().nullable()
    }),
  floor_rate: Yup.number()
    .min(0, () => t("errors.mustBePositive"))
    .nullable(),
  ceiling_rate: Yup.number()
    .min(0, () => t("errors.mustBePositive"))
    .nullable(),
  day_count_convention: Yup.string()
    .required(() => t("errors.required"))
    .oneOf(["actual_actual", "actual_360", "30_360", "actual_365"]),
  interest_reference_day: Yup.string()
    .nullable()
    .when(["float_type", "interest_reference_index"], {
      is: (floatType: string, index: string | null) => floatType === "linked" && !!index,
      then: Yup.string().required(() => t("errors.referenceDayRequired")),
      otherwise: Yup.string().nullable()
    }),
  interest_reference_day_offset: Yup.number()
    .integer(() => t("errors.mustBePositive"))
    .min(0, () => t("errors.mustBePositive"))
    .nullable(),
  commission_rate: Yup.number()
    .min(0, () => t("errors.mustBePositive"))
    .nullable()
});

export const pikValidationSchema = Yup.object({
  pik_base_rate: Yup.number()
    .min(0, () => t("errors.mustBePositive"))
    .nullable(),
  pik_capitalization_frequency: Yup.string().nullable().oneOf(["month", "quarter", "semi-annual", "year"]),
  pik_first_capitalization_date: Yup.string()
    .nullable()
    .when(["is_withdrawal", "pik_base_rate", "pik_reference_index"], {
      is: (isWithdrawal: boolean, baseRate: number | null, referenceIndex: string | null) =>
        isWithdrawal && baseRate !== null && (baseRate > 0 || !!referenceIndex),
      then: Yup.string().required(() => t("errors.pikPaymentDateRequired")),
      otherwise: Yup.string().nullable()
    }),
  pik_reference_index: Yup.string()
    .nullable()
    .when("pik_float_type", {
      is: (floatType: string | null) => floatType !== "fixed" && floatType !== null,
      then: Yup.string().required(() => t("errors.pikLinkageRequired")),
      otherwise: Yup.string().nullable()
    }),
  pik_index_value: Yup.number().nullable(),
  pik_reference_day: Yup.string()
    .nullable()
    .when(["pik_float_type", "pik_reference_index"], {
      is: (floatType: string | null, index: string | null) => floatType === "linked" && !!index,
      then: Yup.string().required(() => t("errors.pikReferenceDayRequired")),
      otherwise: Yup.string().nullable()
    }),
  pik_link_percent: Yup.number()
    .min(0, () => t("errors.mustBePositive"))
    .max(100, () => t("errors.max100"))
    .nullable(),
  pik_floor_rate: Yup.number()
    .min(0, () => t("errors.mustBePositive"))
    .nullable(),
  pik_ceiling_rate: Yup.number()
    .min(0, () => t("errors.mustBePositive"))
    .nullable(),
  pik_float_type: Yup.string().oneOf(["spread", "linked", "fixed"]),
  pik_rate_fixing: Yup.string()
    .nullable()
    .oneOf(["in_arrears", "in_advance", "compounded", null])
    .when("pik_float_type", {
      is: "spread",
      then: Yup.string().required(() => t("errors.rateFixingRequiredSpread")),
      otherwise: Yup.string().nullable()
    }),
  pik_reference_day_offset: Yup.number().integer("Must be an integer").min(0, "Must be positive").nullable(),
  pik_accumulated: Yup.boolean().nullable(),
  interest_on_pik_principal: Yup.boolean().required()
});

export const feesValidationSchema = Yup.object({
  fees: Yup.array().of(
    Yup.object({
      id: Yup.string().required(),
      type: Yup.string().required().oneOf(["credit_facility", "arrangement", "custom_one_time"]),
      start_date: Yup.string(),
      end_date: Yup.string()
        .nullable()
        .when("type", {
          is: "credit_facility",
          then: Yup.string().required(() => t("errors.endDateRequired")),
          otherwise: Yup.string().nullable()
        }),
      frequency: Yup.string()
        .nullable()
        .oneOf(["month", "quarter", "semi-annual", "year", null])
        .when("type", {
          is: "credit_facility",
          then: Yup.string().required(() => t("errors.frequencyRequired")),
          otherwise: Yup.string().nullable()
        }),
      amount: Yup.number()
        .required(() => t("errors.required"))
        .min(0, () => t("errors.mustBePositive")),
      percentage_of: Yup.string().nullable().oneOf(["total_commitment", "unfunded_commitment", "funded_amount", null])
    })
  )
});

export const validationSchema = Yup.object().shape({
  deal_title: Yup.string(),
  ...debtValidationSchema.fields,
  ...principalValidationSchema.fields,
  ...interestValidationSchema.fields,
  ...pikValidationSchema.fields
});

export const facilityValidationSchema = Yup.object().shape({
  deal_title: Yup.string(),
  ...debtValidationSchema.fields,
  ...principalValidationSchema.fields,
  ...interestValidationSchema.fields,
  ...pikValidationSchema.fields,
  ...feesValidationSchema.fields
});

export const prepaymentValidationSchema = Yup.object({
  prepaymentType: Yup.string().required(() => t("errors.prepaymentTypeRequired")),
  effectiveDate: Yup.date().required(() => t("errors.effectiveDateRequired")),
  totalAmount: Yup.number().min(0, () => t("errors.mustBePositive")),
  principalAmount: Yup.number().min(0, () => t("errors.mustBePositive")),
  refinanceDealName: Yup.string().when("prepaymentType", {
    is: "refinance",
    then: Yup.string().required(() => t("errors.dealNameRequired")),
    otherwise: Yup.string()
  }),
  fees: Yup.array().of(
    Yup.object({
      percentage: Yup.number().min(0, () => t("errors.mustBePositive")),
      amount: Yup.number().min(0, () => t("errors.mustBePositive"))
    })
  )
});

type AdditionalFormFields = {
  interest_index_value: number | null;
  principal_index_value: number | null;
  pik_index_value: number | null;
  custom_payments_file: File | null;
  fees: (FacilityFee & { key?: string })[];
};

export type FormValues = DebtSegment & AdditionalFormFields;
